import { api } from "../api";
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function TarefaModal({ isOpen, onClose, lead, onSalvarTarefa }) {
  const usuarioLogado = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuarioLogado")) || {};
    } catch {
      return {};
    }
  }, []);

  const empresaAtiva = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("empresaAtiva")) || {};
    } catch {
      return {};
    }
  }, []);

  const responsavelId = usuarioLogado?.id;
  const empresa_id = empresaAtiva?.id || usuarioLogado?.empresa_id;

  const [novaTarefa, setNovaTarefa] = useState({
    titulo: "",
    descricao: "",
    data_vencimento: "",
    status: "Pendente",
    prioridade: "Médio Potencial",
  });

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [tarefas, setTarefas] = useState([]);
  const [interacoes, setInteracoes] = useState([]);
  const [justificativa, setJustificativa] = useState("");
  const [tarefaParaConcluir, setTarefaParaConcluir] = useState(null);
  const [mostrarConcluidas, setMostrarConcluidas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInteracoes, setLoadingInteracoes] = useState(false);

  const normalizarStatus = (status) =>
    (status || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const limparTexto = (texto) =>
    String(texto || "")
      .replace(/\s+/g, " ")
      .trim();

  const contarPalavras = (texto) =>
    limparTexto(texto).split(" ").filter(Boolean).length;

  const descricaoInteracaoEhValida = (descricao) => {
    const textoLimpo = limparTexto(descricao);
    const palavras = contarPalavras(textoLimpo);
    return textoLimpo.length >= 30 && palavras >= 5;
  };

  const parseDateOnly = (valor) => {
    if (!valor) return null;

    if (valor instanceof Date && !isNaN(valor.getTime())) {
      const d = new Date(valor);
      d.setHours(0, 0, 0, 0);
      return d;
    }

    const s = String(valor).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d, 0, 0, 0, 0);
    }

    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(s)) {
      const [datePart] = s.split(" ");
      const [y, m, d] = datePart.split("-").map(Number);
      return new Date(y, m - 1, d, 0, 0, 0, 0);
    }

    const tentativa = new Date(s);
    if (isNaN(tentativa.getTime())) return null;

    tentativa.setHours(0, 0, 0, 0);
    return tentativa;
  };

  const diffDiasCalendario = (dataA, dataB) => {
    const a = new Date(dataA);
    const b = new Date(dataB);

    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);

    const diffMs = a.getTime() - b.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  };

  const getUltimaInteracaoValida = (lista) => {
    const listaOk = Array.isArray(lista) ? lista : [];

    const validas = listaOk
      .filter((item) => descricaoInteracaoEhValida(item?.descricao))
      .sort((a, b) => {
        const dataA = new Date(
          `${a?.data || "1970-01-01"}T${(a?.hora || "00:00").slice(0, 5)}:00`,
        );
        const dataB = new Date(
          `${b?.data || "1970-01-01"}T${(b?.hora || "00:00").slice(0, 5)}:00`,
        );
        return dataB - dataA;
      });

    return validas[0] || null;
  };

  const validarRegraInteracaoParaTarefa = (listaInteracoes) => {
    const ultimaInteracaoValida = getUltimaInteracaoValida(listaInteracoes);

    if (!ultimaInteracaoValida) {
      return {
        ok: false,
        mensagem:
          "Não é permitido criar tarefa sem interação válida. Registre antes uma interação com pelo menos 30 caracteres e 5 palavras.",
      };
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const dataUltimaInteracao = parseDateOnly(ultimaInteracaoValida.data);

    if (!dataUltimaInteracao) {
      return {
        ok: false,
        mensagem:
          "A última interação válida não tem data válida. Revise o histórico da lead.",
      };
    }

    const diasDesdeUltimaInteracao = diffDiasCalendario(
      hoje,
      dataUltimaInteracao,
    );

    if (diasDesdeUltimaInteracao < 0 || diasDesdeUltimaInteracao > 1) {
      return {
        ok: false,
        mensagem:
          "Só é permitido criar tarefa se a última interação válida da lead tiver sido registrada hoje ou ontem.",
      };
    }

    return {
      ok: true,
      ultimaInteracaoValida,
      diasDesdeUltimaInteracao,
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNovaTarefa((prev) => ({ ...prev, [name]: value }));
  };

  const formatarData = (data) => {
    if (!data) return "";
    return new Date(data + "T00:00:00").toLocaleDateString("pt-BR");
  };

  const carregarInteracoes = async () => {
    if (!lead?.id || !empresa_id) {
      setInteracoes([]);
      return [];
    }

    try {
      setLoadingInteracoes(true);

      const res = await api.get("/interacoes", {
        params: {
          id_lead: lead.id,
          empresa_id,
        },
      });

      const interacoesRecebidas = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];

      setInteracoes(interacoesRecebidas);
      return interacoesRecebidas;
    } catch (error) {
      console.error("❌ Erro ao buscar interações da lead:", error);
      setInteracoes([]);
      return [];
    } finally {
      setLoadingInteracoes(false);
    }
  };

  const carregarTarefas = async () => {
    if (!lead?.id || !empresa_id) return;

    try {
      setLoading(true);

      const res = await api.get("/tarefas", {
        params: {
          lead_id: lead.id,
          empresa_id,
        },
      });

      const tarefasRecebidas = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];

      tarefasRecebidas.sort((a, b) => {
        const dataA = new Date((a.data_vencimento || "") + "T00:00:00");
        const dataB = new Date((b.data_vencimento || "") + "T00:00:00");
        return dataA - dataB;
      });

      setTarefas(tarefasRecebidas);
    } catch (error) {
      console.error("Erro ao buscar tarefas:", error);
      toast.error("Erro ao buscar tarefas");
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && lead?.id && empresa_id) {
      carregarTarefas();
      carregarInteracoes();
    }
    // eslint-disable-next-line
  }, [isOpen, lead?.id, empresaAtiva?.id]);

  useEffect(() => {
    if (!isOpen) {
      setMostrarFormulario(false);
      setTarefaParaConcluir(null);
      setJustificativa("");
      setNovaTarefa({
        titulo: "",
        descricao: "",
        data_vencimento: "",
        status: "Pendente",
        prioridade: "Médio Potencial",
      });
    }
  }, [isOpen]);

  const handleSalvar = async (e) => {
    if (e) e.preventDefault();

    if (!novaTarefa.titulo || !novaTarefa.data_vencimento || !responsavelId) {
      return toast.warning("Preencha título, data e esteja logado!");
    }

    const interacoesAtualizadas =
      interacoes.length > 0 ? interacoes : await carregarInteracoes();

    const validacaoInteracao = validarRegraInteracaoParaTarefa(
      interacoesAtualizadas,
    );
    if (!validacaoInteracao.ok) {
      toast.error(validacaoInteracao.mensagem);
      return;
    }

    const vencimentoMs = new Date(
      novaTarefa.data_vencimento + "T00:00:00",
    ).getTime();

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const hojeMs = hoje.getTime();

    if (vencimentoMs < hojeMs) {
      return toast.error(
        "⛔ A data da tarefa não pode ser anterior à data de hoje.",
      );
    }

    const diffDias = (vencimentoMs - hojeMs) / (1000 * 60 * 60 * 24);

    if (novaTarefa.status !== "Pós Venda" && diffDias > 25) {
      return toast.error(
        "⛔ A data de vencimento não pode ser superior a 25 dias após a criação da tarefa.",
      );
    }

    try {
      const payload = {
        ...novaTarefa,
        titulo: limparTexto(novaTarefa.titulo),
        descricao: limparTexto(novaTarefa.descricao) || "",
        lead_id: lead.id,
        responsavel_id: responsavelId,
        empresa_id,
      };

      await api.post("/tarefas", payload);

      toast.success("✅ Tarefa salva!");
      await carregarTarefas();
      await carregarInteracoes();

      if (typeof onSalvarTarefa === "function") {
        await onSalvarTarefa();
      }

      setNovaTarefa({
        titulo: "",
        descricao: "",
        data_vencimento: "",
        status: "Pendente",
        prioridade: "Médio Potencial",
      });

      setMostrarFormulario(false);
      setMostrarConcluidas(false);
    } catch (err) {
      console.error("❌ Erro ao salvar tarefa:", err);

      const mensagemBackend =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao salvar tarefa.";

      toast.error(`❌ ${mensagemBackend}`);
    }
  };

  const handleCancelar = async (id) => {
    const confirmar = window.confirm(
      "Confirma o cancelamento da tarefa? Ela não será excluída, apenas marcada como cancelada.",
    );

    if (!confirmar) return;

    try {
      await api.put(`/tarefas/${id}`, {
        status: "Cancelado",
        empresa_id,
      });

      toast.success("🚫 Tarefa cancelada com sucesso!");
      await carregarTarefas();

      if (typeof onSalvarTarefa === "function") {
        await onSalvarTarefa();
      }
    } catch (err) {
      console.error("❌ Erro ao cancelar tarefa:", err);
      toast.error(
        err?.response?.data?.detalhes ||
          err?.response?.data?.erro ||
          err?.response?.data?.error ||
          "❌ Erro ao cancelar tarefa.",
      );
    }
  };

  const concluirTarefa = async (id) => {
    if (!justificativa.trim()) {
      toast.warning("📝 Justificativa obrigatória para concluir!");
      return;
    }

    const tarefaOriginal = tarefas.find((t) => t.id === id);

    if (!tarefaOriginal) {
      toast.error("❌ Tarefa não encontrada.");
      return;
    }

    try {
      await api.put(`/tarefas/${id}`, {
        status: "Concluído",
        justificativa: justificativa.trim(),
        empresa_id,
      });

      toast.success("✅ Tarefa concluída!");
      setJustificativa("");
      setTarefaParaConcluir(null);

      await carregarTarefas();

      if (typeof onSalvarTarefa === "function") {
        await onSalvarTarefa();
      }
    } catch (err) {
      console.error("❌ Erro ao concluir tarefa:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "❌ Erro ao concluir tarefa.",
      );
    }
  };

  const ultimaInteracaoValidaPreview = getUltimaInteracaoValida(interacoes);
  const regraInteracaoPreview = validarRegraInteracaoParaTarefa(interacoes);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-40 z-40"
          />

          <motion.div
            key="tarefa-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="fixed top-0 right-0 w-full sm:w-[500px] h-full bg-gradient-to-br from-indigo-900 via-slate-900 to-black text-white z-50 shadow-2xl overflow-y-auto"
          >
            <div className="p-6">
              <div className="mb-6 pb-4 border-b border-slate-600">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      Tarefas atribuídas a{" "}
                      <span className="text-indigo-400">
                        {lead?.nome_lead || "Paciente"}
                      </span>
                    </h2>
                    <p className="text-sm text-slate-400 mt-1">
                      Acompanhe prazos, prioridades e status de acompanhamento.
                    </p>
                  </div>

                  <button
                    onClick={onClose}
                    className="hover:text-red-400"
                    type="button"
                  >
                    <X className="w-6 h-6 text-slate-300" />
                  </button>
                </div>
              </div>

              <div className="mb-4 rounded-xl border border-slate-700 bg-slate-800/60 p-3">
                <p className="text-xs font-semibold text-slate-200 mb-1">
                  Regra para criar tarefa
                </p>

                {loadingInteracoes ? (
                  <p className="text-xs text-slate-400">
                    Validando interações da lead...
                  </p>
                ) : regraInteracaoPreview.ok ? (
                  <div className="text-xs text-green-300">
                    <p>✅ Lead apta para nova tarefa.</p>
                    {ultimaInteracaoValidaPreview?.data && (
                      <p className="mt-1">
                        Última interação válida:{" "}
                        <strong>
                          {new Date(
                            `${ultimaInteracaoValidaPreview.data}T00:00:00`,
                          ).toLocaleDateString("pt-BR")}
                        </strong>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-yellow-300">
                    <p>⚠️ {regraInteracaoPreview.mensagem}</p>
                  </div>
                )}
              </div>

              {!mostrarFormulario && (
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={async () => {
                      const pendentes = tarefas.filter((t) => {
                        const status = normalizarStatus(t.status);
                        return status !== "concluido" && status !== "cancelado";
                      });

                      if (pendentes.length > 0) {
                        return toast.warn(
                          "⚠️ Você já tem uma tarefa pendente. Conclua antes de criar uma nova.",
                        );
                      }

                      const interacoesAtualizadas = await carregarInteracoes();
                      const validacaoInteracao =
                        validarRegraInteracaoParaTarefa(interacoesAtualizadas);

                      if (!validacaoInteracao.ok) {
                        return toast.error(validacaoInteracao.mensagem);
                      }

                      setMostrarFormulario(true);
                    }}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-md shadow transition"
                    type="button"
                  >
                    <Plus className="w-4 h-4" /> Nova Tarefa
                  </button>
                </div>
              )}

              {mostrarFormulario && (
                <form
                  onSubmit={handleSalvar}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6"
                >
                  <input
                    name="titulo"
                    value={novaTarefa.titulo}
                    onChange={handleChange}
                    placeholder="Título"
                    className="col-span-2 rounded-lg px-4 py-2 bg-white text-black border border-indigo-300"
                  />

                  <textarea
                    name="descricao"
                    value={novaTarefa.descricao}
                    onChange={handleChange}
                    placeholder="Descrição"
                    rows={3}
                    className="col-span-2 rounded-lg px-4 py-2 bg-white text-black border border-indigo-300"
                  />

                  <input
                    type="date"
                    name="data_vencimento"
                    value={novaTarefa.data_vencimento}
                    onChange={handleChange}
                    className="col-span-1 rounded-lg px-4 py-2 bg-white text-black border border-indigo-300"
                  />

                  <select
                    name="status"
                    value={novaTarefa.status}
                    onChange={handleChange}
                    className="col-span-1 rounded-lg px-4 py-2 bg-white text-black border border-indigo-300"
                  >
                    <option value="Pendente">Pendente</option>
                    <option value="Em andamento">Em andamento</option>
                    <option value="Concluído">Concluído</option>
                    <option value="Pós Venda">Pós Venda</option>
                  </select>

                  <select
                    name="prioridade"
                    value={novaTarefa.prioridade}
                    onChange={handleChange}
                    className="col-span-2 rounded-lg px-4 py-2 bg-white text-black border border-indigo-300"
                  >
                    <option value="Alto Potencial">Alto Potencial</option>
                    <option value="Médio Potencial">Médio Potencial</option>
                    <option value="Baixo Potencial">Baixo Potencial</option>
                  </select>

                  <div className="col-span-2">
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold shadow-lg"
                    >
                      <Plus className="w-4 h-4" /> Salvar Tarefa
                    </button>
                  </div>
                </form>
              )}

              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => setMostrarConcluidas((prev) => !prev)}
                  className="text-sm px-4 py-2 bg-indigo-700 text-white rounded hover:bg-indigo-800 transition"
                  type="button"
                >
                  {mostrarConcluidas
                    ? "Ocultar Finalizadas"
                    : "Mostrar Finalizadas"}
                </button>
              </div>

              {loading ? (
                <div className="text-center text-slate-400 py-8">
                  Carregando tarefas...
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    const tarefasFiltradas = Array.isArray(tarefas)
                      ? tarefas.filter((t) => {
                          const statusNormalizado = normalizarStatus(t.status);
                          const eConcluida = statusNormalizado === "concluido";
                          const eCancelada = statusNormalizado === "cancelado";

                          if (mostrarConcluidas) {
                            return eConcluida || eCancelada;
                          }

                          return !eConcluida && !eCancelada;
                        })
                      : [];

                    if (tarefasFiltradas.length === 0) {
                      return (
                        <p className="text-sm text-slate-400 italic">
                          {mostrarConcluidas
                            ? "Nenhuma tarefa concluída ou cancelada."
                            : "Nenhuma tarefa cadastrada."}
                        </p>
                      );
                    }

                    return tarefasFiltradas.map((tarefa) => (
                      <div
                        key={tarefa.id}
                        className="bg-white rounded-2xl border border-indigo-200 shadow-md p-4 text-sm text-slate-800 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <p className="font-semibold text-indigo-700 text-base">
                            {tarefa.titulo}
                          </p>

                          <div className="flex items-center gap-2">
                            {normalizarStatus(tarefa.status) !== "concluido" &&
                              normalizarStatus(tarefa.status) !==
                                "cancelado" && (
                                <button
                                  onClick={() => {
                                    setTarefaParaConcluir(tarefa.id);
                                    setJustificativa("");
                                  }}
                                  className="text-green-600 hover:text-green-800 text-xs"
                                  title="Concluir"
                                  type="button"
                                >
                                  ✅
                                </button>
                              )}

                            {normalizarStatus(tarefa.status) !== "concluido" &&
                              normalizarStatus(tarefa.status) !==
                                "cancelado" && (
                                <button
                                  onClick={() => handleCancelar(tarefa.id)}
                                  className="text-red-500 hover:text-red-700 text-xs"
                                  title="Cancelar tarefa"
                                  type="button"
                                >
                                  🚫
                                </button>
                              )}
                          </div>
                        </div>

                        {tarefa.descricao && (
                          <p className="text-slate-600 mb-1 whitespace-pre-wrap">
                            <strong>📝 Descrição:</strong>
                            <br />
                            {tarefa.descricao}
                          </p>
                        )}

                        {tarefa.justificativa && (
                          <p className="text-emerald-600 mb-2 whitespace-pre-wrap">
                            <strong>✅ Conclusão:</strong>
                            <br />
                            {tarefa.justificativa}
                          </p>
                        )}

                        {tarefaParaConcluir === tarefa.id && (
                          <div className="mt-2 space-y-2">
                            <textarea
                              value={justificativa}
                              onChange={(e) => setJustificativa(e.target.value)}
                              placeholder="Digite a justificativa da conclusão..."
                              className="w-full p-2 border border-blue-300 rounded text-sm text-gray-800"
                            />

                            <div className="flex gap-2">
                              <button
                                onClick={() => concluirTarefa(tarefa.id)}
                                className="w-full px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                type="button"
                              >
                                Confirmar Conclusão
                              </button>

                              <button
                                onClick={() => {
                                  setTarefaParaConcluir(null);
                                  setJustificativa("");
                                }}
                                className="w-full px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                                type="button"
                              >
                                Fechar
                              </button>
                            </div>
                          </div>
                        )}

                        {normalizarStatus(tarefa.status) === "concluido" && (
                          <p className="text-green-600 font-semibold mt-2">
                            ✅ Tarefa concluída
                          </p>
                        )}

                        {normalizarStatus(tarefa.status) === "cancelado" && (
                          <p className="text-red-600 font-semibold mt-2">
                            🚫 Tarefa cancelada
                          </p>
                        )}

                        <p className="text-xs text-slate-500 mt-3">
                          📅 Vencimento:{" "}
                          <span className="font-medium">
                            {formatarData(tarefa.data_vencimento)}
                          </span>
                        </p>

                        <div className="flex items-center flex-wrap gap-2 mt-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              normalizarStatus(tarefa.status) === "concluido"
                                ? "bg-green-100 text-green-700"
                                : normalizarStatus(tarefa.status) === "pendente"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : normalizarStatus(tarefa.status) ===
                                        "pos venda" ||
                                      normalizarStatus(tarefa.status) ===
                                        "pos-venda"
                                    ? "bg-sky-100 text-sky-700"
                                    : normalizarStatus(tarefa.status) ===
                                        "cancelado"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {tarefa.status}
                          </span>

                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold shadow ${
                              tarefa.prioridade === "Alto Potencial"
                                ? "bg-amber-600 text-white"
                                : tarefa.prioridade === "Médio Potencial"
                                  ? "bg-yellow-400 text-black"
                                  : "bg-slate-300 text-black"
                            }`}
                          >
                            {tarefa.prioridade === "Alto Potencial" && "💎"}
                            {tarefa.prioridade}
                          </span>

                          <div className="ml-auto flex items-center gap-2">
                            <img
                              src={`https://i.pravatar.cc/40?u=${
                                tarefa.responsavel_nome ||
                                tarefa.responsavel ||
                                tarefa.responsavel_id
                              }`}
                              alt="Avatar"
                              className="w-5 h-5 rounded-full border border-indigo-300"
                            />
                            <span className="text-xs font-medium text-indigo-600">
                              {tarefa.responsavel_nome ||
                                tarefa.responsavel ||
                                "Responsável"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
